// Minimal ADR-020 one-time provisioning adapter.
//
// SECURITY DEPLOYMENT ASSUMPTION: kiosk runs on a dedicated origin with a
// restrictive CSP. localStorage is origin-scoped but still readable by any
// same-origin script, so the kiosk origin must not host unrelated apps or
// third-party scripts. The bearer token is never accepted from VITE_* env,
// logged, included in an Error, or retained in the URL after bootstrap.

export const DEVICE_TOKEN_STORAGE_KEY = "novemai.kiosk.device-token.v1";

const UUID_PATTERN = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const DEVICE_TOKEN_PATTERN = new RegExp(
  `^(?:nkd_|nkd2_${UUID_PATTERN}\\.)${UUID_PATTERN}\\.[A-Za-z0-9_-]{43}$`,
);

function isValidDeviceToken(value: string | null): value is string {
  return value !== null && DEVICE_TOKEN_PATTERN.test(value);
}

export function readProvisionedDeviceToken(): string | null {
  try {
    const stored = window.localStorage.getItem(DEVICE_TOKEN_STORAGE_KEY);
    return isValidDeviceToken(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Capture `#device-token=<encoded>` once. The fragment is scrubbed before any
 * validation/storage work so malformed credentials also disappear immediately
 * from the address bar and browser history surface.
 */
export function captureProvisionedDeviceToken(): string | null {
  const params = new URLSearchParams(window.location.hash.slice(1));
  if (!params.has("device-token")) return readProvisionedDeviceToken();

  const candidate = params.get("device-token");
  window.history.replaceState(
    window.history.state,
    "",
    `${window.location.pathname}${window.location.search}`,
  );

  const existing = readProvisionedDeviceToken();
  if (!isValidDeviceToken(candidate)) return existing;

  try {
    window.localStorage.setItem(DEVICE_TOKEN_STORAGE_KEY, candidate);
    return candidate;
  } catch {
    return existing;
  }
}
